import type { ChipProps } from '@mui/material/Chip';
import MuiChip from '@mui/material/Chip';
import { alpha, styled } from '@mui/material/styles';
import { clsx } from 'clsx';
import type { FC } from 'react';

export interface ExtendedChipProps extends ChipProps {
  /** Custom text color (e.g., '#fff', '#BDCCBE') */
  customColor?: string;
  /** Custom border color (e.g., '#fff', '#BDCCBE') */
  customBorderColor?: string;
}

const StyledChip = styled(MuiChip, {
  shouldForwardProp: (prop) => prop !== 'customColor' && prop !== 'customBorderColor',
})<ExtendedChipProps>(({ theme, customColor, customBorderColor }) => ({
  // Root-level custom color overrides (applies to all variants)
  ...(customColor && {
    color: customColor,
    '& .MuiChip-label': {
      color: customColor,
    },
  }),
  ...(customBorderColor && {
    borderColor: customBorderColor,
  }),

  /**
   * Default Chip Styles
   * -------------------
   */
  '&.MuiChip-colorDefault': {
    ...(customColor && {
      color: customColor,
      '& .MuiChip-label': {
        color: customColor,
      },
    }),
    ...(!customColor && { color: theme.palette.primary.dark }),

    '.MuiAvatar-root': {
      backgroundColor: theme.extendedColors.cement[300],
      color: theme.palette.common.white,
    },

    '.MuiChip-deleteIcon': {
      color: alpha(theme.extendedColors.sage[600] as string, 0.26),

      '&:hover': {
        color: alpha(theme.extendedColors.sage[600] as string, 0.6),
      },
    },
  },

  // Filled Default
  '&.MuiChip-filledDefault': {
    backgroundColor: theme.palette.primary.tonal,

    '&.MuiChip-clickable:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },

  // Outlined Default - override border and color when custom colors are provided
  '&.MuiChip-outlinedDefault': {
    ...(customBorderColor && { borderColor: customBorderColor }),
    ...(!customBorderColor && { borderColor: theme.palette.primary.light }),
    ...(customColor && {
      color: customColor,
      '& .MuiChip-label': {
        color: customColor,
      },
    }),

    '&.MuiChip-clickable:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.4),
    },
  },

  /**
   * Primary Chip Styles
   * -------------------
   */

  /**
   * Secondary Chip Styles
   * -------------------
   */
  '&.MuiChip-colorSecondary': {
    '.MuiAvatar-root': {
      color: theme.palette.common.white,
    },
  },

  // Outlined Secondary
  '&.MuiChip-outlinedSecondary': {
    ...(customBorderColor && { borderColor: customBorderColor }),
    ...(!customBorderColor && { borderColor: theme.palette.secondary.dark }),
    ...(customColor && {
      color: customColor,
      '& .MuiChip-label': {
        color: customColor,
      },
    }),
    ...(!customColor && { color: theme.palette.secondary.dark }),
  },

  /**
   * Error Chip Styles
   * -------------------
   */
  '&.MuiChip-colorError': {
    '.MuiAvatar-root': {
      backgroundColor: theme.palette.error.dark,
      color: theme.palette.common.white,
    },
  },

  /**
   * Warning Chip Styles
   * -------------------
   */
  '&.MuiChip-colorWarning': {
    '.MuiAvatar-root': {
      backgroundColor: theme.palette.warning.dark,
      color: theme.palette.common.white,
    },
  },

  /**
   * Info Chip Styles
   * -------------------
   */
  '&.MuiChip-colorInfo': {
    ...(customColor && {
      color: customColor,
      '& .MuiChip-label': {
        color: customColor,
      },
    }),
    ...(!customColor && { color: theme.palette.info.dark }),

    '.MuiAvatar-root': {
      backgroundColor: theme.palette.info.main,
      color: theme.palette.common.white,
    },
  },

  // Filled Default
  '&.MuiChip-filledInfo': {
    backgroundColor: theme.palette.info.tonal,

    '&.MuiChip-clickable:hover': {
      backgroundColor: theme.extendedColors.bloom[400],
    },

    '.MuiChip-deleteIcon': {
      color: theme.palette.info.main,

      '&:hover': {
        color: theme.palette.info.dark,
      },
    },
  },

  /**
   * Success Chip Styles
   * -------------------
   */
  '&.MuiChip-colorSuccess': {
    '.MuiAvatar-root': {
      backgroundColor: theme.palette.success.dark,
      color: theme.palette.common.white,
    },
  },
}));

const Chip: FC<ExtendedChipProps> = ({
  className,
  customColor,
  customBorderColor,
  sx,
  ...rest
}) => {
  const classes = clsx('mnt-chip', className);

  return (
    <StyledChip
      {...rest}
      className={classes}
      customColor={customColor}
      customBorderColor={customBorderColor}
      sx={sx}
    />
  );
};
Chip.displayName = 'Chip';

export default Chip;
export type { ExtendedChipProps as ChipProps };
